// src/utils/htmlExcerpt.js

/**
 * Equivalente a `buildHighlightedExcerpt` (stringUtils.js), mas para conteúdo
 * HTML: em vez de fatiar a marcação bruta — o que quebraria tag no meio —,
 * projeta primeiro (a mesma régua onde os offsets das notas são medidos, ver
 * `htmlProjection.js`) e usa o MAPA já calculado por ela para reconstruir só
 * o intervalo pedido, preservando negrito/itálico/link.
 *
 * Usado por NotePublicView.vue: o endpoint público devolve o arquivo inteiro
 * (pode ser grande), então um walk só (dentro de `toPlainText`) mais o custo
 * proporcional aos segmentos do trecho — não ao tamanho do documento.
 *
 * Blocos (`p`, `li`, `blockquote`…) não sobrevivem como wrapper aqui: o
 * excerto é pensado para caber numa citação só, sem margem de parágrafo por
 * dentro — a fronteira de bloco vira `<br>`, o mesmo papel que o `\n` da
 * projeção já tinha na versão em texto puro (`white-space: pre-wrap`).
 */

import { toPlainText, BLOCK_TAGS } from "./htmlProjection.js";

const ATTR_ALLOWLIST = {
  a: ["href", "id"],
  span: ["class"],
  td: ["colspan", "rowspan", "colwidth"],
  th: ["colspan", "rowspan", "colwidth"],
};

function copyAllowedAttributes(source, target) {
  const allowed = ATTR_ALLOWLIST[target.localName];
  if (!allowed) return;
  for (const attr of allowed) {
    if (source.hasAttribute(attr)) target.setAttribute(attr, source.getAttribute(attr));
  }
}

/**
 * Envolve o texto sliceado na cadeia de ancestrais INLINE do text node de
 * origem, parando no primeiro bloco (ou na raiz) — é o que preserva
 * `<strong>`/`<em>`/`<a>` sem arrastar `<p>`/`<li>` para dentro do excerto.
 */
function wrapInlineChain(originNode, text) {
  let out = document.createTextNode(text);
  let ancestor = originNode.parentElement;

  while (ancestor && ancestor.localName !== "body" && !BLOCK_TAGS.has(ancestor.localName)) {
    const clone = document.createElement(ancestor.localName);
    copyAllowedAttributes(ancestor, clone);
    if (ancestor.localName === "a") {
      // Abrir em nova aba é decisão de leitura, não de conteúdo — mesmo
      // raciocínio do TextWithNotesHtml.vue.
      clone.setAttribute("target", "_blank");
      clone.setAttribute("rel", "noopener noreferrer");
    }
    clone.appendChild(out);
    out = clone;
    ancestor = ancestor.parentElement;
  }

  return out;
}

/**
 * Reconstrói `[rangeStart, rangeEnd)` da projeção como HTML, a partir do
 * `map` de {@link toPlainText}.
 */
function renderRange(map, rangeStart, rangeEnd) {
  const container = document.createElement("div");

  for (const segment of map) {
    if (segment.end <= rangeStart) continue;
    if (segment.start >= rangeEnd) break;

    if (segment.synthetic) {
      container.appendChild(document.createElement("br"));
      continue;
    }

    const localStart = Math.max(segment.start, rangeStart) - segment.start;
    const localEnd = Math.min(segment.end, rangeEnd) - segment.start;
    const slice = segment.node.data.slice(localStart, localEnd);
    if (!slice) continue;

    container.appendChild(wrapInlineChain(segment.node, slice));
  }

  return container.innerHTML;
}

/**
 * @param {string} html HTML já normalizado (o que está gravado no arquivo).
 * @param {number} start `text_range_start` da nota.
 * @param {number} end `text_range_end` da nota.
 * @param {number} ctx Tamanho do contexto antes/depois, em caracteres da projeção.
 * @returns {{before: string, excerpt: string, after: string, excerptText: string, truncatedBefore: boolean, truncatedAfter: boolean}|null}
 *   `before`/`excerpt`/`after` são HTML — o chamador deve renderizá-los com
 *   `v-html`, nunca com interpolação de texto. `excerptText` é a projeção em
 *   texto puro do MESMO intervalo — não para exibir, mas para quem precisa
 *   classificar o trecho (ex.: "é uma palavra só?") sem contar tag.
 */
export function buildHtmlHighlightedExcerpt(html, start, end, ctx = 100) {
  if (
    typeof html !== "string" ||
    typeof start !== "number" ||
    typeof end !== "number" ||
    start >= end ||
    (start === 0 && end === 0)
  ) {
    return null;
  }

  const root = new DOMParser().parseFromString(html, "text/html").body;
  const { text, map } = toPlainText(root);
  if (!text) return null;

  const safeStart = Math.max(0, start);
  const safeEnd = Math.min(text.length, end);
  if (safeEnd <= safeStart) return null;

  const ctxStart = Math.max(0, safeStart - ctx);
  const ctxEnd = Math.min(text.length, safeEnd + ctx);

  return {
    before: ctxStart < safeStart ? renderRange(map, ctxStart, safeStart) : "",
    excerpt: renderRange(map, safeStart, safeEnd),
    after: safeEnd < ctxEnd ? renderRange(map, safeEnd, ctxEnd) : "",
    excerptText: text.slice(safeStart, safeEnd),
    truncatedBefore: ctxStart > 0,
    truncatedAfter: ctxEnd < text.length,
  };
}
